import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  getBackendLanguage,
  TRANSLATED_STRING_FIELDS,
  translateBackend,
  translateFormCompletionName,
} from '@assets/i18n/config';
import {
  FileDocumentObjectTypes,
  FileDocumentTypes,
} from '@interfaces/fileDocument';

import Link from '@mui/material/Link';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';

import { StatusTag } from '@ovComponents/2-component/statusTag/statusTag';
import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { useFileUpload } from '@providers/fileUploadHook';
import { UserContext, useFeatureFlag } from '@providers/userContextProvider';
import { FlagsmithFlags } from '@utils/flagsmith';
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useSnackbar,
} from 'hephaestus/web';
import {
  CheckCircle,
  Close,
  CloseFullscreen,
  OpenInFull,
  Warning,
} from 'hephaestus/webIcons';
import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ViewFormDialog } from './viewFormDialog';

const FETCH_FORM_COMPLETIONS = gql`
  query fetchFormCompletions($input: FetchFormCompletionsInput!) {
    fetchFormCompletions(input: $input) {
      formCompletions {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        resolvedPdfFileName { ${TRANSLATED_STRING_FIELDS} }
        state
        signingType
        requiredSignatures {
          signerKey
        }
        integrationFormPack {
          id
          state
        }
        user {
          id
          primaryEmail
        }
      }
      totalCount
    }
  }
`;

const SIGN_FORM_COMPLETION = gql`
  mutation signFormCompletion($input: SignFormCompletionInput!) {
    signFormCompletion(input: $input) {
      formCompletion {
        id
        state
      }
    }
  }
`;

const FETCH_INTEGRATION_FORM_PACK_SIGNING_VIEW_URL = gql`
  query FetchIntegrationFormPackSigningViewUrl(
    $input: FetchIntegrationFormPackSigningViewUrlInput!
  ) {
    fetchIntegrationFormPackSigningViewUrl(input: $input) {
      signingViewUrl
    }
  }
`;

/** Return URL required by fetchIntegrationFormPackSigningViewUrl (provider redirect). */
/** Check this variable before deployment of DocuSign in-app signing */
const INTEGRATION_FORM_PACK_SIGNING_RETURN_URL = 'https://www.google.ca';

const normalizeEmail = (email?: string | null): string =>
  email?.trim().toLowerCase() ?? '';

const getSigningPreviewValues = ({
  docusignInAppSigningEnabled,
  currentUserEmail,
  formCompletion,
  packState,
}: {
  docusignInAppSigningEnabled: boolean;
  currentUserEmail?: string | null;
  formCompletion: any;
  packState?: string;
}): { signerKey?: string; canShowSigningPreview: boolean } => {
  const signerKey = formCompletion?.requiredSignatures?.[0]?.signerKey;
  const normalizedCurrentUserEmail = normalizeEmail(currentUserEmail);
  const signerEmail = normalizeEmail(formCompletion?.user?.primaryEmail);
  const isIntendedSigner =
    normalizedCurrentUserEmail.length > 0 &&
    signerEmail.length > 0 &&
    normalizedCurrentUserEmail === signerEmail;

  return {
    signerKey,
    canShowSigningPreview:
      docusignInAppSigningEnabled &&
      isIntendedSigner &&
      packState === 'SENT' &&
      Boolean(signerKey),
  };
};

export const SignFormCompletions = ({
  options,
  onNext,
  workflowCompletion,
}: {
  options: any;
  onNext: () => void;
  workflowCompletion: any;
}) => {
  const { t } = useTranslation(['workflowCompletions', 'client', 'shared']);
  const [activeFormCompletion, setActiveFormCompletion] = useState<any>(null);
  const [activeIndex, setIndex] = useState<any>(0);
  const [file, setFile] = useState<File>();
  const [signLoading, setSignLoading] = useState<boolean>(false);
  const [signingPreviewOpen, setSigningPreviewOpen] = useState(false);
  const [signingPreviewMaximized, setSigningPreviewMaximized] = useState(false);
  const [signingPreviewUrl, setSigningPreviewUrl] = useState<string | null>(
    null,
  );
  const [previewLoadingPackId, setPreviewLoadingPackId] = useState<
    string | null
  >(null);
  const { uploadFile } = useFileUpload();
  const { activeOrganization, userContext } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const docusignInAppSigningEnabled = useFeatureFlag(
    FlagsmithFlags.docusign_in_app_signing,
  );

  const [fetchIntegrationFormPackSigningViewUrl] = useLazyQuery(
    FETCH_INTEGRATION_FORM_PACK_SIGNING_VIEW_URL,
    { fetchPolicy: 'network-only' },
  );

  // Fetch form completions for this workflow
  const { data, loading, refetch } = useQuery(FETCH_FORM_COMPLETIONS, {
    variables: {
      input: {
        filter: {
          workflowCompletionId: workflowCompletion.id,
          states: ['UNSIGNED', 'SIGNED'],
        },
        pagination: {
          perPage: 100, // Fetch all forms for this workflow
          sortField: `translatedName.${getBackendLanguage()}`,
          sortDesc: false,
        },
      },
    },
    skip: !workflowCompletion?.id,
    pollInterval: 30000,
    fetchPolicy: 'no-cache',
  });

  const formCompletions = data?.fetchFormCompletions?.formCompletions || [];
  // Separate regular forms from integration forms
  const forms =
    formCompletions.filter((f: any) => f.signingType !== 'INTEGRATION') ?? [];

  // Group integration forms by pack
  const integrationForms =
    formCompletions.filter((f: any) => f.signingType === 'INTEGRATION') ?? [];
  const groupedPacks = integrationForms.reduce((acc: any, form: any) => {
    const packId = form.integrationFormPack?.id;
    if (!packId) return acc;

    if (!acc[packId]) {
      acc[packId] = {
        id: packId,
        state: form.integrationFormPack?.state || 'SENT',
        forms: [],
      };
    }
    acc[packId].forms.push(form);
    return acc;
  }, {});
  const packs = Object.values(groupedPacks);

  const [signFormCompletion] = useMutation(SIGN_FORM_COMPLETION, {
    onCompleted: () => {
      // Refetch form completions to get updated state
      refetch();

      if (activeIndex + 1 < forms.length) {
        setIndex(activeIndex + 1);
        setActiveFormCompletion(forms[activeIndex + 1]);
      } else {
        setActiveFormCompletion(null);
      }
    },
  });

  const handleSign = async () => {
    setSignLoading(true);
    if (file) {
      await uploadFile(file, {
        objectType: FileDocumentObjectTypes.FORM_COMPLETION_SIGNED,
        objectId: activeFormCompletion?.id,
        sourceId: activeFormCompletion?.id,
        sourceType: 'FORM_COMPLETION',
        fileName: file.name,
        mediaType: file.type,
        type: FileDocumentTypes.TEMPLATE,
        permissionType: 'PUBLIC',
        name: file.name,
        organizationId: activeOrganization.id,
      });
      setFile(undefined);
    }
    await signFormCompletion({
      variables: {
        input: {
          formCompletionId: activeFormCompletion?.id,
        },
      },
    });
    setSignLoading(false);
  };

  const openSigningPreviewForPack = async (
    pack: any,
    signerKeyFromForm?: string,
  ) => {
    if (!docusignInAppSigningEnabled) return;
    const integrationFormPackId = pack?.id;
    const signerKey =
      signerKeyFromForm ??
      pack?.forms?.[0]?.requiredSignatures?.[0]?.signerKey ??
      undefined;
    if (!integrationFormPackId || !signerKey) {
      enqueueSnackbar(t('client:signingViewUrlMissing'), {
        variant: 'warning',
      });
      return;
    }
    setPreviewLoadingPackId(integrationFormPackId);
    try {
      const { data, error } = await fetchIntegrationFormPackSigningViewUrl({
        variables: {
          input: {
            integrationFormPackId,
            signerKey,
            returnUrl: INTEGRATION_FORM_PACK_SIGNING_RETURN_URL,
          },
        },
      });
      if (error) {
        enqueueSnackbar(error.message, { variant: 'error' });
        return;
      }
      const url =
        data?.fetchIntegrationFormPackSigningViewUrl?.signingViewUrl?.trim() ??
        '';
      if (!url) {
        enqueueSnackbar(t('client:signingViewUrlMissing'), {
          variant: 'warning',
        });
        return;
      }
      setSigningPreviewUrl(url);
      setSigningPreviewMaximized(false);
      setSigningPreviewOpen(true);
    } finally {
      setPreviewLoadingPackId(null);
    }
  };

  const closeSigningPreview = () => {
    setSigningPreviewOpen(false);
    setSigningPreviewUrl(null);
    setSigningPreviewMaximized(false);
    // Signing can complete inside the embedded DocuSign view; refresh statuses on close.
    void refetch();
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: '60vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <WorkflowCompletionForm
      onSubmit={() => onNext()}
      submitButtonProps={{
        label: t('continue'),
        disabled:
          !options.allowContinuingWithoutSigning &&
          (forms.map((x: any) => x.state).includes('UNSIGNED') ||
            packs?.some((pack: any) => pack.state !== 'COMPLETED')),
        dataTestId: 'sign-continue-button',
      }}
    >
      <Box sx={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
        {options?.title && (
          <Typography variant='displayLarge' sx={{ mt: 1 }}>
            {translateBackend(options?.title)}
          </Typography>
        )}
        {options?.subtitle && (
          <Typography
            variant='bodyLarge'
            component='div'
            sx={{
              mb: 3,
              table: { width: '100%' },
              display: 'inline-flex',
              alignItems: 'end',
            }}
          >
            <Box
              sx={{
                display: 'inline-block',
              }}
            >
              <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
                {translateBackend(options?.subtitle)}
              </ReactMarkdown>
            </Box>
          </Typography>
        )}
        <Table sx={{ mb: 3 }}>
          <TableBody data-testid='documents-table'>
            <TableRow>
              <TableHeadCell sx={{ width: '100%' }}>
                {t('client:document')}
              </TableHeadCell>
              <TableHeadCell sx={{ minWidth: '140px' }}>
                {t('client:status')}
              </TableHeadCell>
            </TableRow>
            {forms.map((formCompletion: any, index: number) => (
              <TableRow
                data-testid={`document-${index}`}
                key={index}
                hover
                pointer
                onClick={() => {
                  setActiveFormCompletion(formCompletion);
                  setIndex(index);
                }}
              >
                <TableCell>
                  {translateFormCompletionName(formCompletion)}
                </TableCell>
                <TableCell>
                  <StatusTag
                    color={
                      formCompletion.state === 'SIGNED' ? 'positive' : 'warning'
                    }
                    icon={
                      formCompletion.state === 'SIGNED' ? CheckCircle : Warning
                    }
                    label={
                      formCompletion.state === 'SIGNED'
                        ? t('client:signed')
                        : t('client:notSigned')
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
            {packs?.map((pack: any, packIndex: number) => (
              <Fragment key={`pack-${packIndex}`}>
                <TableRow hover>
                  <TableCell>
                    <Typography variant='bodyLarge'>
                      {t('client:formPackage')}
                    </Typography>
                    <ul>
                      {pack.forms?.map(
                        (formCompletion: any, formIndex: number) => {
                          const { signerKey, canShowSigningPreview } =
                            getSigningPreviewValues({
                              docusignInAppSigningEnabled,
                              currentUserEmail: userContext?.email,
                              formCompletion,
                              packState: pack.state,
                            });

                          return (
                            <li key={`pack-${packIndex}-form-${formIndex}`}>
                              {translateFormCompletionName(formCompletion)}
                              {/* only show signing preview if the user is the signer and the pack is sent */}
                              {canShowSigningPreview && (
                                <>
                                  {' '}
                                  (
                                  <Link
                                    component='button'
                                    type='button'
                                    variant='body2'
                                    disabled={previewLoadingPackId === pack.id}
                                    onClick={() =>
                                      void openSigningPreviewForPack(
                                        pack,
                                        signerKey,
                                      )
                                    }
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    {previewLoadingPackId === pack.id
                                      ? t('shared:loading')
                                      : t('client:signingViewPreview')}
                                  </Link>
                                  )
                                </>
                              )}
                            </li>
                          );
                        },
                      )}
                    </ul>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <StatusTag
                        color={
                          pack.state === 'COMPLETED' ? 'positive' : 'warning'
                        }
                        icon={
                          pack.state === 'COMPLETED' ? CheckCircle : Warning
                        }
                        label={t(`client:formPackStates.${pack.state}`)}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
        {activeFormCompletion && (
          <ViewFormDialog
            formCompletionId={activeFormCompletion.id}
            onClose={() => setActiveFormCompletion(null)}
            sign={handleSign}
            file={file}
            setFile={setFile}
            signLoading={signLoading}
          />
        )}
        <Dialog
          open={signingPreviewOpen}
          onClose={closeSigningPreview}
          fullWidth
          fullScreen={signingPreviewMaximized}
          maxWidth='lg'
        >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant='bodyLarge'>
                {t('client:signingPreviewDialogTitle')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  aria-label={signingPreviewMaximized ? 'minimize' : 'maximize'}
                  onClick={() =>
                    setSigningPreviewMaximized((prevMax) => !prevMax)
                  }
                  data-testid='signing-preview-maximize-button'
                >
                  {signingPreviewMaximized ? (
                    <CloseFullscreen fontSize='inherit' />
                  ) : (
                    <OpenInFull fontSize='inherit' />
                  )}
                </IconButton>
                <IconButton
                  aria-label='close'
                  onClick={closeSigningPreview}
                  data-testid='signing-preview-close-button'
                >
                  <Close fontSize='inherit' />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              height: 'min(80vh, 800px)',
              p: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {signingPreviewUrl ? (
              <Box sx={{ flex: 1, width: '100%', minHeight: 480 }}>
                <iframe
                  title={t('client:signingPreviewDialogTitle')}
                  src={signingPreviewUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: 480,
                    border: 'none',
                  }}
                  sandbox='allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals'
                />
              </Box>
            ) : null}
          </DialogContent>
        </Dialog>
      </Box>
    </WorkflowCompletionForm>
  );
};
